import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBug, faFilter, faSync, faChevronDown, faChevronRight,
  faCheckCircle, faTimesCircle, faExclamationCircle, faRedo,
  faRobot, faPen, faTrash, faWifi, faSearch, faDownload,
  faBolt, faSquare, faCheckSquare
} from '@fortawesome/free-solid-svg-icons';
import webFindingService, { severityConfig, statusConfig } from '../../services/webFindingService';
import httpExchangeService from '../../services/httpExchangeService';
import { useLanguage } from '../../contexts/LanguageContext';
import { findingTitle, findingDescription, findingRemediation, evidenceTitle } from './findingI18n';

// HttpExchange headers are stored as an array of { name, value }. Older/imported
// data may use a plain object. Render either without producing "[object Object]".
const formatHeaders = (headers) => {
  if (!headers) return '';
  if (Array.isArray(headers)) return headers.map(h => `${h?.name}: ${h?.value}`).join('\n');
  if (typeof headers === 'object') return Object.entries(headers).map(([k, v]) => `${k}: ${v}`).join('\n');
  return '';
};

const WebFindingsList = ({ webTarget, isDarkMode }) => {
  const { translations, language, setLanguage } = useLanguage();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [retesting, setRetesting] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ id: null, status: '', notes: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [creatingActions, setCreatingActions] = useState(false);
  // Exchanges are lazy-loaded on expand (the list no longer ships full bodies).
  const [exchangeCache, setExchangeCache] = useState({}); // exchangeId -> exchange | 'loading'

  const toggleExpand = (finding) => {
    const opening = expandedId !== finding._id;
    setExpandedId(opening ? finding._id : null);
    const exId = finding.httpExchangeId;
    if (opening && exId && exchangeCache[exId] === undefined) {
      setExchangeCache(prev => ({ ...prev, [exId]: 'loading' }));
      httpExchangeService.getById(exId)
        .then(ex => setExchangeCache(prev => ({ ...prev, [exId]: ex })))
        .catch(() => setExchangeCache(prev => ({ ...prev, [exId]: null })));
    }
  };

  useEffect(() => {
    if (webTarget?._id) loadFindings();
  }, [webTarget]);

  const loadFindings = async () => {
    try {
      setLoading(true);
      const data = await webFindingService.getAll({ webTargetId: webTarget._id });
      setFindings(data);
    } catch (err) {
      console.error('Error loading findings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetest = async (id) => {
    setRetesting(id);
    try {
      const { finding } = await webFindingService.retest(id);
      setFindings(prev => prev.map(f => f._id === id ? finding : f));
    } catch (err) {
      console.error('Retest error:', err);
    } finally {
      setRetesting(null);
    }
  };

  const handleStatusChange = async (id, status) => {
    if (status === 'custom') {
      setStatusUpdate({ id, status: '', notes: '' });
      return;
    }
    try {
      const updated = await webFindingService.updateStatus(id, status);
      setFindings(prev => prev.map(f => f._id === id ? updated : f));
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const submitStatusUpdate = async () => {
    if (!statusUpdate.id || !statusUpdate.status) return;
    try {
      const updated = await webFindingService.updateStatus(statusUpdate.id, statusUpdate.status, statusUpdate.notes);
      setFindings(prev => prev.map(f => f._id === statusUpdate.id ? updated : f));
      setStatusUpdate({ id: null, status: '', notes: '' });
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await webFindingService.remove(id);
      setFindings(prev => prev.filter(f => f._id !== id));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (!webTarget?._id) return;
    setExporting(true);
    try {
      await webFindingService.exportOpenJson(webTarget._id, { lang: language });
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const SEV_RANK = { critical: 4, high: 3, medium: 2, low: 1, informational: 0 };
  const filtered = findings.filter(f => {
    if (filterSeverity !== 'all' && f.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = [f.title, f.description, f.endpoint?.url, f.endpoint?.method, f.severity, f.status, f.cweId, f.wstgTest?.testId]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    const d = (SEV_RANK[b.severity] ?? -1) - (SEV_RANK[a.severity] ?? -1);
    if (d !== 0) return d;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const severityCounts = {};
  findings.forEach(f => { severityCounts[f.severity] = (severityCounts[f.severity] || 0) + 1; });

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(f => f._id)));
    }
  };

  const handleCreateActions = async (findingIds) => {
    setCreatingActions(true);
    try {
      const result = await webFindingService.createActions(findingIds);
      const msg = [];
      if (result.created > 0) msg.push(`${result.created} action${result.created > 1 ? 's' : ''} created`);
      if (result.skipped > 0) msg.push(`${result.skipped} skipped (already exists)`);
      alert(msg.join(', ') || 'Done');
      setSelected(new Set());
    } catch (err) {
      console.error('Create actions error:', err);
      alert('Failed to create actions: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreatingActions(false);
    }
  };

  const inputClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-3 py-2 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBug} className="text-red-400" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Findings ({findings.length})
          </span>
          {/* Severity summary chips */}
          <div className="flex gap-1 ml-2">
            {['critical', 'high', 'medium', 'low', 'informational'].map(sev => {
              const count = severityCounts[sev] || 0;
              if (count === 0) return null;
              const sc = severityConfig[sev];
              return (
                <span key={sev} className={`text-[9px] px-1.5 py-0.5 rounded ${sc.bg}`}>
                  {count} {sc.label}
                </span>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Language toggle — verify German rendering live before export */}
          <div className={`flex rounded overflow-hidden border text-[10px] ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            {['en', 'de'].map(lng => (
              <button
                key={lng}
                onClick={() => setLanguage(lng)}
                className={`px-1.5 py-1 font-medium ${language === lng
                  ? 'bg-teal-600 text-white'
                  : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
          {selected.size > 0 && (
            <button
              onClick={() => handleCreateActions([...selected])}
              disabled={creatingActions}
              title="Create Red Team Actions from selected findings"
              className={`px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50`}
            >
              <FontAwesomeIcon icon={creatingActions ? faSync : faBolt} spin={creatingActions} />
              Add to Actions ({selected.size})
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting || findings.length === 0}
            title="Export findings as OpenJSON for the reporting app"
            className={`px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
          >
            <FontAwesomeIcon icon={exporting ? faSync : faDownload} spin={exporting} />
            Export
          </button>
          <button onClick={loadFindings} className={`p-1.5 rounded text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className={`flex items-center gap-3 px-3 py-1.5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <button onClick={toggleSelectAll} className={`text-[11px] ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} title={selected.size === filtered.length ? 'Deselect all' : 'Select all'}>
          <FontAwesomeIcon icon={selected.size > 0 && selected.size === filtered.length ? faCheckSquare : faSquare} />
        </button>
        <FontAwesomeIcon icon={faFilter} className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className={`text-[10px] rounded border px-1.5 py-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
          <option value="all">All Severities</option>
          {Object.entries(severityConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`text-[10px] rounded border px-1.5 py-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
          <option value="all">All Statuses</option>
          {Object.entries(statusConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <div className="relative flex-1 max-w-xs">
          <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search findings (title, URL, CWE…)"
            className={`w-full pl-6 pr-2 py-0.5 text-[10px] rounded border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300 placeholder-gray-600' : 'bg-white border-gray-200 text-gray-600 placeholder-gray-400'}`}
          />
        </div>
        <span className={`text-[10px] ml-auto ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          {filtered.length} of {findings.length}
        </span>
      </div>

      {/* Findings list */}
      <div className="flex-1 overflow-auto">
        {filtered.map(finding => {
          const isExpanded = expandedId === finding._id;
          const sc = severityConfig[finding.severity] || severityConfig.informational;
          const st = statusConfig[finding.status] || statusConfig.new;

          return (
            <div key={finding._id} className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              {/* Finding row */}
              <div
                onClick={() => toggleExpand(finding)}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
              >
                <button
                  onClick={(e) => toggleSelect(finding._id, e)}
                  className={`text-[11px] ${selected.has(finding._id) ? 'text-orange-400' : isDarkMode ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-500'}`}
                >
                  <FontAwesomeIcon icon={selected.has(finding._id) ? faCheckSquare : faSquare} />
                </button>
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  className={`text-[10px] w-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}
                />
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${sc.bg}`}>
                  {sc.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {findingTitle(finding, translations)}
                    {finding.source === 'exposiq_import' ? (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-600 text-white align-middle" title="Imported from ExposIQ">ExposIQ</span>
                    ) : finding.source === 'passive_scan' ? (
                      <FontAwesomeIcon icon={faWifi} className="ml-1.5 text-teal-400 text-[10px]" title="Passive scan (proxy traffic)" />
                    ) : finding.autoGenerated ? (
                      <FontAwesomeIcon icon={faRobot} className="ml-1.5 text-purple-400 text-[10px]" title="Auto-generated (AuthSmash)" />
                    ) : null}
                    {(finding.tags || []).includes('exposiq-confirmed') && finding.source !== 'exposiq_import' && (
                      <span className="ml-1 px-1 py-0.5 rounded text-[8px] font-semibold bg-blue-900/40 text-blue-300 align-middle" title="Also confirmed by ExposIQ">+ExposIQ</span>
                    )}
                  </div>
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {finding.endpoint?.method} {finding.endpoint?.url?.replace(/^https?:\/\/[^/]+/, '') || finding.endpoint?.path}
                    {finding.wstgTest?.testId && (
                      <span className={`ml-2 px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {finding.wstgTest.testId}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg}`}>
                  {st.label}
                </span>
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                  {new Date(finding.createdAt).toLocaleDateString()}
                </span>
              </div>

              {/* Expanded detail */}
              {isExpanded && (() => {
                const rawEx = finding.httpExchange || (finding.httpExchangeId ? exchangeCache[finding.httpExchangeId] : null);
                const exLoading = rawEx === 'loading';
                const exchange = (rawEx && rawEx !== 'loading') ? rawEx : null;
                return (
                <div className={`px-6 py-3 space-y-3 ${isDarkMode ? 'bg-gray-850' : 'bg-gray-50'}`}>
                  {/* Description */}
                  <div>
                    <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Description</div>
                    <div className={`text-xs whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{findingDescription(finding, translations)}</div>
                  </div>

                  {/* DAST coverage manifest — what was fuzzed and how */}
                  {finding.dastManifest && Array.isArray(finding.dastManifest.endpoints) && (
                    <div>
                      <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        DAST Coverage — {finding.dastManifest.endpoints.length} endpoint{finding.dastManifest.endpoints.length === 1 ? '' : 's'} fuzzed
                      </div>
                      <div className={`text-[11px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {finding.dastManifest.engine && <span>Engine: <span className="font-mono">{finding.dastManifest.engine}</span> · </span>}
                        {finding.dastManifest.authenticated ? '🔒 authenticated' : 'unauthenticated'}
                        {Array.isArray(finding.dastManifest.checks) && finding.dastManifest.checks.length > 0 && (
                          <span> · checks: {finding.dastManifest.checks.join(', ')}</span>
                        )}
                      </div>
                      <div className={`max-h-72 overflow-auto rounded border ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                        <table className="w-full text-[11px]">
                          <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            <tr>
                              <th className="text-left font-medium px-2 py-1 w-16">Method</th>
                              <th className="text-left font-medium px-2 py-1">URL</th>
                              <th className="text-left font-medium px-2 py-1">Checks</th>
                            </tr>
                          </thead>
                          <tbody className="font-mono">
                            {finding.dastManifest.endpoints.map((ep, i) => (
                              <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                                <td className={`px-2 py-1 align-top font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ep.method || 'GET'}</td>
                                <td className={`px-2 py-1 align-top break-all ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ep.url}</td>
                                <td className={`px-2 py-1 align-top ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {Array.isArray(ep.checks) && ep.checks.length > 0
                                    ? ep.checks.join(', ')
                                    : <span className="italic opacity-60">no injectable params</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Evidence — highlights/snippets first (most relevant on top),
                      then the full request/response below. */}
                  <div>
                    <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Evidence</div>

                    {/* Key snippets / decoded content (array evidence: passive scan, AuthSmash, ExposIQ) */}
                    {Array.isArray(finding.evidence) && finding.evidence.filter(e => e && (e.content || e.title)).length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {finding.evidence.filter(e => e && (e.content || e.title)).map((ev, i) => (
                          <div key={i} className={`rounded border p-2 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                            {(ev.title || ev.labelKey) && <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{evidenceTitle(ev, translations)}</div>}
                            {ev.content && <pre className={`whitespace-pre-wrap [overflow-wrap:anywhere] font-mono text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ev.content}</pre>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Evidence highlight marker (points at the relevant byte range) */}
                    {finding.evidenceMarker && (
                      <div className={`px-2 py-1.5 mb-2 text-[11px] rounded border ${isDarkMode ? 'border-yellow-800 bg-yellow-900/20 text-yellow-300' : 'border-yellow-200 bg-yellow-50 text-yellow-700'}`}>
                        <strong>⚡ Highlight:</strong> <span className="font-mono [overflow-wrap:anywhere]">{finding.evidenceMarker}</span>
                      </div>
                    )}

                    {/* String / object evidence fallback */}
                    {finding.evidence && typeof finding.evidence === 'string' && (
                      <div className={`text-xs font-mono p-2 mb-2 rounded ${isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-600'}`}>
                        <pre className="whitespace-pre-wrap [overflow-wrap:anywhere]">{finding.evidence}</pre>
                      </div>
                    )}
                    {finding.evidence && typeof finding.evidence === 'object' && !Array.isArray(finding.evidence) && (
                      <div className={`text-xs font-mono p-2 mb-2 rounded ${isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-600'}`}>
                        {finding.evidence.baselineStatusCode && <div>Baseline: {finding.evidence.baselineStatusCode}</div>}
                        {finding.evidence.testedStatusCode && <div>Tested: {finding.evidence.testedStatusCode}</div>}
                        {finding.evidence.enforcement && <div>Enforcement: {finding.evidence.enforcement}</div>}
                        {finding.evidence.comparisonDetails?.reason && <div>Reason: {finding.evidence.comparisonDetails.reason}</div>}
                      </div>
                    )}

                    {/* Loading indicator while the exchange lazy-loads */}
                    {exLoading && (
                      <div className={`text-xs flex items-center gap-2 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <FontAwesomeIcon icon={faSync} spin /> Loading request/response…
                      </div>
                    )}

                    {/* Full request / response (context, below the highlights) */}
                    {exchange && (
                      <div className={`rounded overflow-hidden border ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                        <div className={`px-3 py-1 text-[10px] font-medium border-b ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>
                          Full request / response
                        </div>
                        <div className="flex" style={{ height: '360px' }}>
                          {/* Left: Request */}
                          <div className={`flex-1 min-w-0 flex flex-col border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                            <div className={`px-3 py-1.5 border-b font-medium text-xs flex items-center gap-2 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-teal-400' : 'bg-gray-100 border-gray-300 text-teal-600'}`}>
                              ↑ Request
                            </div>
                            <div className={`flex-1 overflow-auto p-3 font-mono text-[11px] ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>
                              <pre className="whitespace-pre-wrap [overflow-wrap:anywhere]">
{exchange.request.method} {exchange.request.path || exchange.request.url} HTTP/1.1
{formatHeaders(exchange.request.headers)}
{exchange.request.body && '\n' + exchange.request.body}
                              </pre>
                            </div>
                          </div>

                          {/* Right: Response */}
                          <div className="flex-1 min-w-0 flex flex-col">
                            <div className={`px-3 py-1.5 border-b font-medium text-xs flex items-center gap-2 ${
                              exchange.response.statusCode >= 200 && exchange.response.statusCode < 300
                                ? (isDarkMode ? 'bg-gray-800 border-gray-700 text-green-400' : 'bg-gray-100 border-gray-300 text-green-600')
                                : (isDarkMode ? 'bg-gray-800 border-gray-700 text-orange-400' : 'bg-gray-100 border-gray-300 text-orange-600')
                            }`}>
                              ↓ Response {exchange.response.statusCode}
                              {exchange.response.body && (
                                <span className={`ml-auto text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {(exchange.response.body.length / 1024).toFixed(1)}KB
                                </span>
                              )}
                            </div>
                            <div className={`flex-1 overflow-auto p-3 font-mono text-[11px] ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>
                              <pre className="whitespace-pre-wrap [overflow-wrap:anywhere]">
HTTP/1.1 {exchange.response.statusCode} {exchange.response.statusText || exchange.response.statusMessage || ''}
{formatHeaders(exchange.response.headers)}
{exchange.response.body && '\n' + exchange.response.body}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* No evidence */}
                    {!exchange && !exLoading && (!finding.evidence || (Array.isArray(finding.evidence) && finding.evidence.filter(e => e && (e.content || e.title)).length === 0)) && !finding.evidenceMarker && (
                      <div className={`text-xs italic p-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                        No evidence recorded
                      </div>
                    )}
                  </div>

                  {/* References */}
                  {finding.references && finding.references.length > 0 && (
                    <div>
                      <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>References</div>
                      <div className="text-xs space-y-0.5">
                        {(Array.isArray(finding.references) ? finding.references : String(finding.references).split('\n')).filter(Boolean).map((r, i) => (
                          <div key={i}><a href={r} target="_blank" rel="noreferrer" className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline break-all`}>{r}</a></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Remediation */}
                  {finding.remediation && (
                    <div>
                      <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Remediation</div>
                      <div className={`text-xs whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{findingRemediation(finding, translations)}</div>
                    </div>
                  )}

                  {/* CWE / WSTG / MITRE */}
                  <div className="flex flex-wrap gap-4 text-[10px]">
                    {finding.cweId && (
                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                        CWE: {finding.cweId} {finding.cweName && `(${finding.cweName})`}
                      </span>
                    )}
                    {finding.wstgTest?.testId && (
                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                        WSTG: {finding.wstgTest.testId} - {finding.wstgTest.testName}
                      </span>
                    )}
                    {Array.isArray(finding.mitreMapping) && finding.mitreMapping.length > 0 && (
                      finding.mitreMapping.map((m, mi) => (
                        <span key={mi} className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                          ATT&CK: {m.techniqueId} {m.techniqueName}
                        </span>
                      ))
                    )}
                  </div>

                  {/* Retest history */}
                  {finding.retestResults?.length > 0 && (
                    <div>
                      <div className={`text-[10px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Retest History</div>
                      <div className="space-y-1">
                        {finding.retestResults.map((rt, i) => (
                          <div key={i} className={`flex items-center gap-3 text-[10px] px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                            <FontAwesomeIcon
                              icon={rt.stillVulnerable ? faExclamationCircle : faCheckCircle}
                              className={rt.stillVulnerable ? 'text-red-400' : 'text-green-400'}
                            />
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                              {new Date(rt.timestamp).toLocaleString()}
                            </span>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                              Status: {rt.statusCode}, {rt.contentLength}b
                            </span>
                            <span className={rt.stillVulnerable ? 'text-red-400' : 'text-green-400'}>
                              {rt.stillVulnerable ? 'Still vulnerable' : 'Possibly fixed'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status update form */}
                  {statusUpdate.id === finding._id && (
                    <div className={`p-2 rounded border space-y-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <select
                        value={statusUpdate.status}
                        onChange={e => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                        className={inputClass}
                      >
                        <option value="">Select status...</option>
                        {Object.entries(statusConfig).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                      <textarea
                        value={statusUpdate.notes}
                        onChange={e => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Notes..."
                        rows={2}
                        className={`w-full ${inputClass}`}
                      />
                      <div className="flex gap-2">
                        <button onClick={submitStatusUpdate} className="px-2 py-1 bg-teal-600 text-white rounded text-[10px]">Save</button>
                        <button onClick={() => setStatusUpdate({ id: null, status: '', notes: '' })} className={`px-2 py-1 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRetest(finding._id)}
                      disabled={retesting === finding._id}
                      className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      <FontAwesomeIcon icon={faRedo} spin={retesting === finding._id} />
                      Retest
                    </button>
                    <button
                      onClick={() => handleStatusChange(finding._id, 'custom')}
                      className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      <FontAwesomeIcon icon={faPen} />
                      Update Status
                    </button>
                    {finding.status === 'new' && (
                      <button
                        onClick={() => handleStatusChange(finding._id, 'confirmed')}
                        className="px-2 py-1 rounded text-[10px] flex items-center gap-1 bg-red-600/20 text-red-400 hover:bg-red-600/30"
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        Confirm
                      </button>
                    )}
                    {finding.status === 'new' && (
                      <button
                        onClick={() => handleStatusChange(finding._id, 'false_positive')}
                        className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                      >
                        <FontAwesomeIcon icon={faTimesCircle} />
                        False Positive
                      </button>
                    )}
                    {(finding.status === 'confirmed' || finding.status === 'open') && (
                      <button
                        onClick={() => handleStatusChange(finding._id, 'remediated')}
                        className="px-2 py-1 rounded text-[10px] flex items-center gap-1 bg-green-600/20 text-green-400 hover:bg-green-600/30"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Mark Remediated
                      </button>
                    )}
                    <button
                      onClick={() => handleCreateActions([finding._id])}
                      disabled={creatingActions}
                      className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 ${isDarkMode ? 'bg-orange-900/30 text-orange-300 hover:bg-orange-900/50' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                      title="Create Red Team Action from this finding"
                    >
                      <FontAwesomeIcon icon={faBolt} />
                      Add to Actions
                    </button>
                    <button
                      onClick={() => handleDelete(finding._id)}
                      className="px-2 py-1 rounded text-[10px] flex items-center gap-1 text-red-400 hover:bg-red-900/20 ml-auto"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          );
        })}

        {filtered.length === 0 && !loading && (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            <FontAwesomeIcon icon={faBug} className="text-2xl mb-2" />
            <div className="text-sm">No findings yet</div>
            <div className="text-xs mt-1">Run an authorization test or add findings manually.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebFindingsList;
